/**
 * The Material Symbols Rounded ligatures this app renders through `PjIcon`.
 *
 * Not the admin app's shared ~280-icon vocabulary — a small, closed set kept
 * here so a call site can only ever name one of these, and the type checker
 * catches a typo instead of a runtime fallback glyph.
 *
 * The brand mark is not here: `PjMark` renders it directly from its own
 * dedicated SVG symbols (`pj-mark`, `pj-logo`, and their dark/flat variants),
 * unrelated to this ligature-driven set — it is solid-mass brand artwork, not
 * a stroke icon, and was never reachable through `PJ_ICON` to begin with.
 */
const PJ_ICON = {
  TAKEAWAY: 'shopping_bag',
  DINE_IN: 'dining',
  SCAN: 'qr_code_scanner',
  ORDERS: 'receipt_long',
  YOU: 'person',
  ADD: 'add',
  MINUS: 'remove',
  SEARCH: 'search',
  BACK: 'arrow_back',
  CHEVRON_DOWN: 'expand_more',
  CLOSE: 'close',
  LOCATION: 'location_on',
  CLOCK: 'schedule',
  CALENDAR: 'calendar_month',
  CHECK: 'check',
  NOTE: 'edit_note',
  LOGOUT: 'logout',
  GUESTS: 'group',
  INFO: 'info',
  BIRTHDAY: 'cake',
  ANNIVERSARY: 'favorite',
  ENGAGEMENT: 'diamond',
  BUSINESS: 'business_center'
} as const

type PjIconName = (typeof PJ_ICON)[keyof typeof PJ_ICON]

/**
 * Sizes are named rather than numeric so a caller cannot invent one. 24 in the
 * tab bar, 20 inline and in buttons, 18 in meta rows. Never below 16. `text`
 * follows the type it sits in, for an icon inside a display-size line.
 */
const PJ_ICON_SIZE = {
  NAV: 'nav',
  INLINE: 'inline',
  META: 'meta',
  TEXT: 'text'
} as const

type PjIconSize = (typeof PJ_ICON_SIZE)[keyof typeof PJ_ICON_SIZE]

interface PjIconProps {
  name: PjIconName
  size?: PjIconSize
  /**
   * Screen-reader label. Omit for decoration — an icon beside its own word is
   * decoration, and announcing it twice is worse than not announcing it.
   */
  label?: string
}

export { PJ_ICON, PJ_ICON_SIZE }
export type { PjIconName, PjIconProps, PjIconSize }
